import React from "react";
import { Target } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { formatDateTimeDisplay, resolveDisplayTimezone } from "../../lib/datetime";
import { normalizeInstrumentSymbol } from "../../lib/instruments";

const DASHBOARD_TYPOGRAPHY = {
  sectionTitle: "text-xs font-semibold uppercase tracking-[0.18em] text-white/65",
};

const formatDiagnosticsTimestamp = (value, timeZone) => {
  if (!value) return "";
  return formatDateTimeDisplay(value, timeZone, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const OrderEntryPanel = ({
  selectedTradingAccount,
  account,
  timeZone,
  selectedTradingAccountId,
  activeAccountContractLink,
  symbol,
  compact = false,
  orderEntry, // Hook object from useOrderEntry
  orderDiagnostics,
  orderDiagnosticsLoading,
}) => {
  const {
    orderType, setOrderType,
    orderSide, setOrderSide,
    quantity, setQuantity,
    price, setPrice,
    stopLoss, setStopLoss,
    takeProfit, setTakeProfit,
    placeOrder,
    isSubmitting
  } = orderEntry;

  const displayTimeZone = resolveDisplayTimezone(timeZone);
  const effectiveAccountId = selectedTradingAccountId || account?.account_id || null;
  const diagnosticsMatchCurrentContext = Boolean(orderDiagnostics) && (
    normalizeInstrumentSymbol(orderDiagnostics?.symbol) === normalizeInstrumentSymbol(symbol)
    && (
      !effectiveAccountId
      || !orderDiagnostics?.trading_account_id
      || String(orderDiagnostics.trading_account_id) === String(effectiveAccountId)
    )
  );
  const currentDiagnostics = diagnosticsMatchCurrentContext ? orderDiagnostics : null;
  const preflight = currentDiagnostics?.preflight || null;
  const latestExecutionError = currentDiagnostics?.latest_local_execution_error || null;
  const diagnosticsRefreshing = orderDiagnosticsLoading && Boolean(currentDiagnostics);
  const quoteGuardrail = preflight?.quote_guardrail || null;

  const renderBrokerDiagnosticsBody = () => {
    if (preflight) {
      return (
        <>
          <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
            <span className={`rounded border px-2 py-1 ${preflight.authentication_ok ? "border-green-500/30 bg-green-500/10 text-green-300" : "border-red-500/30 bg-red-500/10 text-red-300"}`}>
              {preflight.authentication_ok ? "Auth OK" : "Auth failed"}
            </span>
            <span className={`rounded border px-2 py-1 ${preflight.contract_resolved ? "border-green-500/30 bg-green-500/10 text-green-300" : "border-amber-500/30 bg-amber-500/10 text-amber-300"}`}>
              {preflight.contract_resolved ? "Contract resolved" : "Contract unresolved"}
            </span>
            <span className="rounded border border-white/10 bg-white/5 px-2 py-1 text-white/70">
              {preflight.open_position_count || 0} broker position(s)
            </span>
            <span className="rounded border border-white/10 bg-white/5 px-2 py-1 text-white/70">
              {preflight.open_order_count || 0} working order(s)
            </span>
            {quoteGuardrail ? (
              <span className={`rounded border px-2 py-1 ${
                quoteGuardrail.fresh
                  ? "border-green-500/30 bg-green-500/10 text-green-300"
                  : "border-amber-500/30 bg-amber-500/10 text-amber-300"
              }`}>
                {quoteGuardrail.fresh
                  ? `Quote fresh${quoteGuardrail.age_seconds != null ? ` (${quoteGuardrail.age_seconds}s)` : ""}`
                  : `Quote stale${quoteGuardrail.age_seconds != null ? ` (${quoteGuardrail.age_seconds}s)` : ""}`}
              </span>
            ) : null}
          </div>
          <div
            className={`mt-2 min-h-[14px] text-[11px] leading-[14px] transition-opacity ${
              diagnosticsRefreshing ? "text-white/60 opacity-100" : "text-white/0 opacity-0"
            }`}
            aria-live="polite"
            aria-hidden={!diagnosticsRefreshing}
          >
            Refreshing broker status...
          </div>
          {quoteGuardrail && !quoteGuardrail.fresh ? (
            <div className="mt-2 text-xs text-amber-300">
              Live submit guardrail is active. Orders wait for a fresh broker quote before they can be queued.
            </div>
          ) : null}
          {quoteGuardrail?.available ? (
            <div className="mt-1 text-[11px] text-white/65">
              Quote source: {quoteGuardrail.source || "unknown"}
              {formatDiagnosticsTimestamp(quoteGuardrail.timestamp, displayTimeZone)
                ? ` • Updated ${formatDiagnosticsTimestamp(quoteGuardrail.timestamp, displayTimeZone)}`
                : ""}
            </div>
          ) : null}
          {preflight.open_position_side ? (
            <div className="mt-2 text-xs text-white/72">
              Broker currently reports an open {String(preflight.open_position_side).toUpperCase()} position on {symbol}.
            </div>
          ) : null}
          {preflight.error ? (
            <div className="mt-2 text-xs text-amber-300">
              {preflight.error}
            </div>
          ) : null}
          {latestExecutionError ? (
            <div className="mt-2 rounded border border-red-500/25 bg-red-500/10 px-3 py-2 text-xs text-red-200">
              Latest Topstep rejection{formatDiagnosticsTimestamp(latestExecutionError.updated_at, displayTimeZone) ? ` (${formatDiagnosticsTimestamp(latestExecutionError.updated_at, displayTimeZone)})` : ""}: {latestExecutionError.message}
            </div>
          ) : null}
        </>
      );
    }
    if (orderDiagnosticsLoading) {
      return <div className="mt-2 text-xs text-white/72">Checking broker and account status...</div>;
    }
    return (
      <div className="mt-2 text-xs text-white/72">
        Broker diagnostics unavailable for the selected account.
      </div>
    );
  };

  return (
    <Card className="bg-white/5 border-white/10">
      <CardHeader className={compact ? "px-4 pb-2 pt-4" : "pb-2"}>
        <CardTitle className={`${DASHBOARD_TYPOGRAPHY.sectionTitle} flex items-center gap-2`}>
          <Target className="h-4 w-4" />
          Place Order
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        {/* Order Route Info */}
        <div className="rounded-md border border-white/10 bg-black/20 px-3 py-2">
          <div className="text-[10px] uppercase tracking-[0.14em] text-white/65">Order Route</div>
          <div className={`mt-1 grid gap-2 text-sm text-white ${compact ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"}`}>
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-white/65">Broker</div>
              <div className="font-medium text-white">
                {selectedTradingAccount?.broker_display_name || account?.broker_display_name || "No broker connected"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-white/65">Contract</div>
              <div className="font-medium text-white">
                {activeAccountContractLink?.broker_contract_label || activeAccountContractLink?.primary_symbol || symbol}
              </div>
            </div>
          </div>
          <div className={`mt-2 grid gap-2 text-xs text-white/72 ${compact ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"}`}>
            <div>{compact ? "Uses the selected mobile trading context." : "Uses the account selected in the header."}</div>
            <div className={compact ? "text-left" : "text-right sm:text-left"}>
              {activeAccountContractLink?.strategy_name
                ? `Linked strategy: ${activeAccountContractLink.strategy_name}`
                : "Manual order only — no linked strategy for this account + contract"}
            </div>
          </div>
        </div>

        {/* Broker Diagnostics / Checks */}
        {!selectedTradingAccountId && !account?.account_id ? null : (
          <div className="rounded-md border border-white/10 bg-black/20 px-3 py-2">
            <div className="flex items-center justify-between gap-3 text-[10px] uppercase tracking-[0.14em] text-white/65">
              <span>Broker Check</span>
              {latestExecutionError ? (
                <span className="rounded border border-red-500/25 bg-red-500/10 px-2 py-1 text-[10px] normal-case tracking-normal text-red-200">
                  Rejection on file
                </span>
              ) : null}
            </div>
            {renderBrokerDiagnosticsBody()}
          </div>
        )}

        {/* Inputs */}
        <div className="grid grid-cols-2 gap-2">
          <Select value={orderType} onValueChange={setOrderType}>
            <SelectTrigger className="bg-white/5 border-white/10 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-black border-white/10">
              <SelectItem value="MARKET" className="text-white focus:bg-white/10">Market</SelectItem>
              <SelectItem value="LIMIT" className="text-white focus:bg-white/10">Limit</SelectItem>
            </SelectContent>
          </Select>

          <Select value={orderSide} onValueChange={setOrderSide}>
            <SelectTrigger className="bg-white/5 border-white/10 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-black border-white/10">
              <SelectItem value="BUY" className="text-white focus:bg-white/10">Buy</SelectItem>
              <SelectItem value="SELL" className="text-white focus:bg-white/10">Sell</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
             <label className="text-[10px] uppercase tracking-wider text-white/60">Qty</label>
             <Input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="Qty"
                className="bg-white/5 border-white/10 text-white"
              />
          </div>
          {orderType === "LIMIT" && (
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-wider text-white/60">Price</label>
              <Input
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="Price"
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
           <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-wider text-white/60">Stop Loss</label>
              <Input
                type="number"
                value={stopLoss}
                onChange={(e) => setStopLoss(e.target.value)}
                placeholder="Stop Loss"
                className="bg-white/5 border-white/10 text-white"
              />
           </div>
           <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-wider text-white/60">Take Profit</label>
              <Input
                type="number"
                value={takeProfit}
                onChange={(e) => setTakeProfit(e.target.value)}
                placeholder="Take Profit"
                className="bg-white/5 border-white/10 text-white"
              />
           </div>
        </div>

        <Button
          onClick={placeOrder}
          disabled={(!selectedTradingAccountId && !account?.account_id) || isSubmitting}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white disabled:bg-white/10 disabled:text-white/60"
        >
          {isSubmitting ? "Placing Order..." : "Place Order"}
        </Button>
      </CardContent>
    </Card>
  );
};

export default React.memo(OrderEntryPanel);
